import { useCallback, useEffect, useState } from 'react';
import type { AssetModelMetadata } from '@evimo/types';
import { AssetModelService } from '~/app/services/asset-model/asset-model.service';
import {
  AssetTypeService,
  type AssetTypeMetadata,
} from '~/app/services/asset-type/asset-type.service';

type UseAssetImportReferenceDataParams = {
  setErrorAction: (value: string | null) => void;
};

export function useAssetImportReferenceData(
  params: UseAssetImportReferenceDataParams,
) {
  const { setErrorAction } = params;
  const [assetTypes, setAssetTypes] = useState<AssetTypeMetadata[]>([]);
  const [assetModels, setAssetModels] = useState<AssetModelMetadata[]>([]);
  const [selectedAssetTypeId, setSelectedAssetTypeId] = useState('');
  const [selectedAssetModelId, setSelectedAssetModelId] = useState('');
  const [isLoadingAssetTypes, setIsLoadingAssetTypes] = useState(false);
  const [isLoadingAssetModels, setIsLoadingAssetModels] = useState(false);

  const resetSelection = useCallback(() => {
    setSelectedAssetTypeId('');
    setSelectedAssetModelId('');
    setAssetModels([]);
    setIsLoadingAssetModels(false);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadAssetTypes = async () => {
      setIsLoadingAssetTypes(true);

      try {
        const result = await AssetTypeService.findAll();

        if (!isMounted) {
          return;
        }

        setAssetTypes(result);
      } catch (nextError) {
        if (!isMounted) {
          return;
        }

        setErrorAction(
          nextError instanceof Error
            ? nextError.message
            : 'Failed to load asset types',
        );
      } finally {
        if (isMounted) {
          setIsLoadingAssetTypes(false);
        }
      }
    };

    void loadAssetTypes();

    return () => {
      isMounted = false;
    };
  }, [setErrorAction]);

  useEffect(() => {
    let isMounted = true;

    const loadAssetModels = async () => {
      if (!selectedAssetTypeId) {
        setAssetModels([]);
        setSelectedAssetModelId('');
        setIsLoadingAssetModels(false);
        return;
      }

      setIsLoadingAssetModels(true);
      setSelectedAssetModelId('');

      try {
        const result =
          await AssetModelService.findAllByAssetTypeId(selectedAssetTypeId);

        if (!isMounted) {
          return;
        }

        setAssetModels(result);
      } catch (nextError) {
        if (!isMounted) {
          return;
        }

        setAssetModels([]);
        setErrorAction(
          nextError instanceof Error
            ? nextError.message
            : 'Failed to load asset models',
        );
      } finally {
        if (isMounted) {
          setIsLoadingAssetModels(false);
        }
      }
    };

    void loadAssetModels();

    return () => {
      isMounted = false;
    };
  }, [selectedAssetTypeId, setErrorAction]);

  return {
    assetModels,
    assetTypes,
    isLoadingAssetModels,
    isLoadingAssetTypes,
    resetSelection,
    selectedAssetModelId,
    selectedAssetTypeId,
    setSelectedAssetModelId,
    setSelectedAssetTypeId,
  };
}
